/**
 * API client do novo Suprimentos (Plano de Suprimentos, Fase 5):
 * sobras/boxes, ajustes com aprovação, inventário, configuração e
 * solicitações de cadastro. Espelha `estoque/urls.py`.
 *
 * Fica separado de `estoque.js` (produto + movimento) de propósito: cada
 * arquivo cobre uma família de endpoints e muda por uma razão só.
 */
import { ACCESS_API_URL } from "../config/api";

function comQuery(prefixo, params = {}) {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    usp.set(k, String(v));
  });
  const qs = usp.toString();
  return `${prefixo}${qs ? `?${qs}` : ""}`;
}

/** Listas vêm como `{results}`, `{data}` (paginação por envelope) ou cruas. */
export function linhasDe(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

const post = (request, url, body) =>
  request(url, { method: "POST", body: JSON.stringify(body || {}) });

function baixarBlob(blob, nome) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ---------- Boxes ----------

const PREF_BOX = "/api/boxes";

export function listarBoxes(request, params = {}) {
  return request(comQuery(PREF_BOX, { ativo: params.ativo, page_size: params.pageSize || 500 }));
}
export function criarBox(request, dados) {
  return post(request, PREF_BOX, dados);
}
export function atualizarBox(request, id, dados) {
  return request(`${PREF_BOX}/${id}`, { method: "PATCH", body: JSON.stringify(dados) });
}
export function desativarBox(request, id) {
  return post(request, `${PREF_BOX}/${id}/desativar`);
}
/** Apaga de vez; 409 `em_uso` quando ha sobra/inventario apontando. */
export function apagarBox(request, id) {
  return request(`${PREF_BOX}/${id}`, { method: "DELETE", silentError: true });
}

// ---------- Sobras ----------

const PREF_SOBRA = "/api/sobras";

export const ESTADOS_SOBRA = [
  { value: "prevista", label: "Prevista", tone: "warning" },
  { value: "disponivel", label: "Disponível", tone: "success" },
  { value: "reservada", label: "Reservada", tone: "info" },
  { value: "usada", label: "Usada", tone: "neutral" },
  { value: "descartada", label: "Descartada", tone: "danger" },
  { value: "indisponivel", label: "Indisponível", tone: "neutral" },
];
export const ESTADO_SOBRA_LABEL = Object.fromEntries(ESTADOS_SOBRA.map((e) => [e.value, e.label]));
export const ESTADO_SOBRA_TONE = Object.fromEntries(ESTADOS_SOBRA.map((e) => [e.value, e.tone]));

export function listarSobras(request, params = {}) {
  return request(comQuery(PREF_SOBRA, {
    estado: params.estado,
    produto: params.produto,
    box: params.box,
    origem: params.origem,
    busca: params.busca,
    page_size: params.pageSize || 500,
  }));
}
export function obterSobra(request, id) {
  return request(`${PREF_SOBRA}/${id}`);
}
export function criarSobra(request, dados) {
  return post(request, `${PREF_SOBRA}/criar`, dados);
}
/** Apaga várias sobras de vez (usada/reservada são recusadas pelo backend). */
const LOTE_EXCLUSAO = 200;

/**
 * Apaga várias sobras em fatias de 200 (limite do backend) e soma o resultado:
 * { apagadas, recusadas: [{codigo, motivo}], resumo: {usada: n, reservada: n} }.
 */
export async function excluirSobras(request, ids) {
  const total = { apagadas: 0, recusadas: [], resumo: {} };
  for (let i = 0; i < ids.length; i += LOTE_EXCLUSAO) {
    const r = await post(request, `${PREF_SOBRA}/excluir`, { ids: ids.slice(i, i + LOTE_EXCLUSAO) });
    total.apagadas += r.apagadas || 0;
    total.recusadas.push(...(r.recusadas || []));
    Object.entries(r.resumo || {}).forEach(([k, n]) => { total.resumo[k] = (total.resumo[k] || 0) + n; });
  }
  return total;
}
/** Edita medidas/veio/box/observação de sobra já cadastrada (produto não muda). */
export function editarSobra(request, id, dados) {
  return post(request, `${PREF_SOBRA}/${id}/editar`, dados);
}
export function moverSobraParaBox(request, id, boxId) {
  return post(request, `${PREF_SOBRA}/${id}/mover-box`, { box: boxId || null });
}
export function reservarSobra(request, id, ref = "") {
  return post(request, `${PREF_SOBRA}/${id}/reservar`, { ref });
}
export function liberarSobra(request, id, ref = "") {
  return post(request, `${PREF_SOBRA}/${id}/liberar`, { ref });
}
export function usarSobra(request, id, ref = "") {
  return post(request, `${PREF_SOBRA}/${id}/usar`, { ref });
}
export function desmarcarUsoSobra(request, id) {
  return post(request, `${PREF_SOBRA}/${id}/desmarcar-uso`);
}
export function descartarSobra(request, id, motivo = "") {
  return post(request, `${PREF_SOBRA}/${id}/descartar`, { motivo });
}
export function definirDisponibilidadeSobra(request, id, disponivel) {
  return post(request, `${PREF_SOBRA}/${id}/disponibilidade`, { disponivel });
}
export function confirmarSobraPrevista(request, id, { existe, box }) {
  return post(request, `${PREF_SOBRA}/${id}/confirmar`, { existe, box: box || null });
}
export function listarSobrasPrevistas(request) {
  return request(`${PREF_SOBRA}/previstas`);
}
export function listarExportacoesSobras(request) {
  return request("/api/sobras-exportacoes?page_size=100");
}

/**
 * Exporta as sobras marcadas para o .xlsx do otimizador. Blob não passa
 * pelo wrapper `request` (mesma razão de `baixarXlsLote`). Devolve o
 * código da exportação registrada no backend.
 */
export async function exportarSobrasXlsx(ids) {
  const session = JSON.parse(localStorage.getItem("pharuerp:access-session") || "{}");
  const headers = { "Content-Type": "application/json" };
  if (session?.accessToken) headers.Authorization = `Bearer ${session.accessToken}`;
  const resp = await fetch(`${ACCESS_API_URL}${PREF_SOBRA}/exportar`, {
    method: "POST",
    headers,
    body: JSON.stringify({ ids }),
  });
  if (!resp.ok) {
    let detalhe = "";
    try {
      detalhe = (await resp.json())?.detalhe || "";
    } catch (_) {
      detalhe = "";
    }
    throw new Error(detalhe || `Falha ao exportar sobras (${resp.status}).`);
  }
  const blob = await resp.blob();
  const disposicao = resp.headers.get("Content-Disposition") || "";
  const nome = /filename="([^"]+)"/.exec(disposicao)?.[1] || "sobras.xlsx";
  baixarBlob(blob, nome);
  return resp.headers.get("X-Exportacao-Codigo");
}

// ---------- Ajustes ----------

export function listarMotivosAjuste(request) {
  return request("/api/motivos-ajuste?page_size=200");
}
export function criarMotivoAjuste(request, dados) {
  return post(request, "/api/motivos-ajuste", dados);
}
export function atualizarMotivoAjuste(request, id, dados) {
  return request(`/api/motivos-ajuste/${id}`, { method: "PATCH", body: JSON.stringify(dados) });
}
export function listarAjustesPendentes(request, params = {}) {
  return request(comQuery("/api/ajustes-pendentes", { estado: params.estado, page_size: 200 }));
}
export function aprovarAjuste(request, id) {
  return post(request, `/api/ajustes-pendentes/${id}/aprovar`);
}
export function recusarAjuste(request, id, motivo) {
  return post(request, `/api/ajustes-pendentes/${id}/recusar`, { motivo });
}

// ---------- Configuração ----------

export function obterConfiguracaoSuprimentos(request) {
  return request("/api/suprimentos/configuracao");
}
export function salvarConfiguracaoSuprimentos(request, dados) {
  return request("/api/suprimentos/configuracao", { method: "PUT", body: JSON.stringify(dados) });
}

// ---------- Inventário ----------

const PREF_INV = "/api/inventarios";

export const ESTADOS_INVENTARIO = [
  { value: "ABERTA", label: "Aberta", tone: "neutral" },
  { value: "CONTAGEM", label: "Em contagem", tone: "info" },
  { value: "RECONTAGEM", label: "Em recontagem", tone: "info" },
  { value: "AGUARDANDO_APROVACAO", label: "Aguardando aprovação", tone: "warning" },
  { value: "ENCERRADA", label: "Encerrada", tone: "success" },
  { value: "CANCELADA", label: "Cancelada", tone: "danger" },
];
export const ESTADO_INV_LABEL = Object.fromEntries(ESTADOS_INVENTARIO.map((e) => [e.value, e.label]));
export const ESTADO_INV_TONE = Object.fromEntries(ESTADOS_INVENTARIO.map((e) => [e.value, e.tone]));

export function listarInventarios(request, params = {}) {
  return request(comQuery(PREF_INV, { estado: params.estado, page_size: 100 }));
}
export function obterInventario(request, id) {
  return request(`${PREF_INV}/${id}`);
}
export function abrirInventario(request, dados) {
  return post(request, `${PREF_INV}/abrir`, dados);
}
export function iniciarContagemInventario(request, id) {
  return post(request, `${PREF_INV}/${id}/iniciar-contagem`);
}
export function adicionarItemInventario(request, id, produtoId) {
  return post(request, `${PREF_INV}/${id}/adicionar-item`, { produto: produtoId });
}
export function contarInventario(request, id, itens) {
  return post(request, `${PREF_INV}/${id}/contar`, { itens });
}
export function enviarInventarioParaAprovacao(request, id) {
  return post(request, `${PREF_INV}/${id}/enviar-aprovacao`);
}
/** Quem aprova dispensa a 2a contagem (todos os pendentes, ou so `itens`), com justificativa. */
export function dispensarRecontagemInventario(request, id, justificativa, itens = null) {
  return post(request, `${PREF_INV}/${id}/dispensar-recontagem`, itens ? { justificativa, itens } : { justificativa });
}
export function aprovarInventario(request, id) {
  return post(request, `${PREF_INV}/${id}/aprovar`);
}
export function cancelarInventario(request, id, motivo) {
  return post(request, `${PREF_INV}/${id}/cancelar`, { motivo });
}

// ---------- Solicitações de cadastro ----------

const PREF_SC = "/api/solicitacoes-cadastro";

export const ESTADOS_SOLIC_CADASTRO = [
  { value: "PENDENTE", label: "Pendente", tone: "warning" },
  { value: "EM_ANALISE", label: "Em análise", tone: "info" },
  { value: "CADASTRADO", label: "Cadastrado", tone: "success" },
  { value: "REJEITADO", label: "Rejeitado", tone: "danger" },
  { value: "CANCELADO", label: "Cancelado", tone: "neutral" },
];
export const ESTADO_SC_LABEL = Object.fromEntries(ESTADOS_SOLIC_CADASTRO.map((e) => [e.value, e.label]));
export const ESTADO_SC_TONE = Object.fromEntries(ESTADOS_SOLIC_CADASTRO.map((e) => [e.value, e.tone]));

export function listarSolicitacoesCadastro(request, params = {}) {
  return request(comQuery(PREF_SC, { estado: params.estado, fonte: params.fonte, page_size: 200 }));
}
export function criarSolicitacaoCadastro(request, dados) {
  return post(request, `${PREF_SC}/criar`, dados);
}
export function iniciarAnaliseSolicitacaoCadastro(request, id) {
  return post(request, `${PREF_SC}/${id}/iniciar-analise`);
}
export function cadastrarDeSolicitacao(request, id, dados) {
  return post(request, `${PREF_SC}/${id}/cadastrar`, dados);
}
export function rejeitarSolicitacaoCadastro(request, id, motivo) {
  return post(request, `${PREF_SC}/${id}/rejeitar`, { motivo });
}
export function cancelarSolicitacaoCadastro(request, id) {
  return post(request, `${PREF_SC}/${id}/cancelar`);
}
